import { useId } from "react";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { keyframes, useTheme } from "@mui/material/styles";
import { brand, Card, Icon, Typography } from "@platform/design-system";

import { formatRunStartTime } from "../../format";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** One full ". → .. → ... → none" cycle. */
const ELLIPSIS_CYCLE = "1.6s";

// Each dot holds its opacity until the next keyframe (step-end), so the cycle
// reads as four discrete frames rather than a fade. A dot shows from its own
// frame through the "..." frame, and all three clear for the last.
const dots = [
  { id: "first", frames: keyframes`0% { opacity: 1; } 75% { opacity: 0; }` },
  {
    id: "second",
    frames: keyframes`0% { opacity: 0; } 25% { opacity: 1; } 75% { opacity: 0; }`,
  },
  {
    id: "third",
    frames: keyframes`0% { opacity: 0; } 50% { opacity: 1; } 75% { opacity: 0; }`,
  },
];

/**
 * Trailing dots that count up and clear in a loop. Every dot keeps its width
 * while hidden, so the centred heading never shifts as they change.
 */
const AnimatedEllipsis = (): React.JSX.Element => (
  <Box component="span" aria-hidden>
    {dots.map(({ id, frames }) => (
      <Box
        key={id}
        component="span"
        sx={{
          animation: `${frames} ${ELLIPSIS_CYCLE} step-end infinite`,
          [`@media ${REDUCED_MOTION}`]: { animation: "none", opacity: 1 },
        }}
      >
        .
      </Box>
    ))}
  </Box>
);

/** The icon's own coordinate space; catalog icons draw on a 24×24 viewBox. */
const ICON_VIEWBOX = 24;

/**
 * The sparkle with a highlight band sweeping across it. The band is a moving
 * SVG gradient used as the icon's fill, so it lights only the sparkle's shape
 * rather than the square around it.
 */
const ShimmeringSparkle = (): React.JSX.Element => {
  const theme = useTheme();
  const reducedMotion = useMediaQuery(REDUCED_MOTION);
  const gradientId = useId();
  const base = brand("supporting")(theme);
  // Tinted from the brand colour rather than a fixed hex, so it follows the theme.
  const highlight = `color-mix(in srgb, ${base} 35%, white)`;

  return (
    <>
      <Box
        component="svg"
        aria-hidden
        sx={{ position: "absolute", width: 0, height: 0 }}
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={0}
            y1={0}
            x2={ICON_VIEWBOX}
            y2={ICON_VIEWBOX}
          >
            <stop offset="0.35" style={{ stopColor: base }} />
            <stop offset="0.5" style={{ stopColor: highlight }} />
            <stop offset="0.65" style={{ stopColor: base }} />
            {/* Starts and ends a full icon-width off the shape, so the band
                enters and leaves cleanly before the loop restarts. */}
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from={`${-ICON_VIEWBOX} ${-ICON_VIEWBOX}`}
              to={`${ICON_VIEWBOX} ${ICON_VIEWBOX}`}
              dur="2s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      </Box>
      {/* The catalog's sizes stop at large; the design calls for 96px. */}
      <Box
        sx={{
          display: "flex",
          "& > svg": {
            fontSize: 96,
            fill: reducedMotion ? base : `url(#${gradientId})`,
          },
        }}
      >
        <Icon name="aiWorking" />
      </Box>
    </>
  );
};

interface Props {
  startedAt: Date;
}

/** What the Launchpad shows while Rumi works on the RFP. */
const RumiRunning = ({ startedAt }: Props): React.JSX.Element => (
  <Card padding="spacious" data-testid="rumi-running">
    <Stack spacing={2} alignItems="center" textAlign="center">
      <ShimmeringSparkle />
      <Typography variant="display-medium" component="h2">
        Rumi is working on this RFP
        <AnimatedEllipsis />
      </Typography>
      <Typography
        variant="body-large"
        color="textSecondary"
        sx={{ maxWidth: 568 }}
      >
        Run started {formatRunStartTime(startedAt)}. Runs take between 12
        minutes and an hour. Editing is locked until the run finishes. Check
        back then. This page fills in with Rumi&apos;s read.
      </Typography>
    </Stack>
  </Card>
);

export { RumiRunning };
export type { Props as RumiRunningProps };
